import { useUpdate } from 'ahooks';
import { toInteger } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { DrawSvg as SVGRenderer } from '@/utils/draw-svg-kit';
import { fetchVectorText, getVectorTextFromCache } from '@/utils/file-kit';
import { getLayerInnerStyle } from '@/utils/layer-kit';
import { VectorType, type IVectorLayerProps } from '@/version-types';
import { type VectorLayerProps } from '../types';
import {
    checkIsUsingEffect,
    createGraphSVGImage,
    createNormalSVGImage,
    getSimpleRectSVGString,
    normalizeBorderRadius,
} from '../utils';

type Props = VectorLayerProps & {
    /**
     * 是否作为菜单图标
     */
    asMenuIcon?: boolean;
};

// FIXME: 暂时没搞懂为什么要做，初步看下来感觉可以统一成一种形式
// svg 内容渲染策略：
// 1. 如果 vectorType 为 ellipse，使用 SVGRenderer 渲染，即直接使用 svg 元素渲染
// 2. 如果 vectorType 为 polygon 或者 rect，并且同时拥有特效时，使用 SVG 作为内容渲染
// 3. 其余情况使用 image 元素渲染

type SVGImage = {
    width: number;
    height: number;
    src: string;
};

// FIXME: normalSVGImage 依赖中有引用类型，会触发重渲染

const ImageContent: React.FC<Props> = ({ layer, asMenuIcon = false, onRenderEnd }) => {
    const { id, width, height, opacity } = layer;
    const layerData = layer.data as IVectorLayerProps;
    const {
        vectorType,
        fill,
        fillGradient,
        stroke,
        strokeGradient,
        strokeWidth,
        strokeDasharray,
        strokeLinecap,
        blur,
        effect,
        strokeHidden,
        url,
        colors,
        naturalWidth,
        naturalHeight,
        fillRule,
        imageTransform: { widthRatio, heightRatio, flipH, flipV, offsetX: offsetXRatio, offsetY: offsetYRatio, scale },
    } = layerData;
    const borderRadius = normalizeBorderRadius(layerData);
    const originImgWidth = toInteger(width * widthRatio);
    const originImgHeight = toInteger(height * heightRatio);
    const offsetX = toInteger(offsetXRatio * originImgWidth * scale);
    const offsetY = toInteger(offsetYRatio * originImgHeight * scale);
    const innerStyle = getLayerInnerStyle(originImgWidth, originImgHeight, offsetX, offsetY, scale, flipH, flipV);
    const containerStyle = { ...innerStyle, opacity };

    const isUsingEffect = checkIsUsingEffect(layerData, { includeBlur: true });

    const svgStringCache = getVectorTextFromCache(url);
    const hasSVGStringCache = !!svgStringCache;

    const updateManual = useUpdate();

    // FIXME: 几个引用类型作为依赖会导致重复计算，暂时先注释
    // 初始化 normalSVGImage
    const normalSVGImage = useMemo<SVGImage | null>(() => {
        if (vectorType === VectorType.Ellipse || vectorType === VectorType.Graph) return null;
        if (vectorType !== VectorType.Rect && !hasSVGStringCache) return null; // FIXME: 不懂什么意思

        const svgSourceString =
            vectorType === VectorType.Rect
                ? getSimpleRectSVGString(width, height, {
                      naturalWidth,
                      naturalHeight,
                      borderRadius,
                      strokeWidth: strokeHidden ? 0 : strokeWidth,
                  })
                : getVectorTextFromCache(url);

        if (!svgSourceString) return null;

        return createNormalSVGImage(svgSourceString, layer);
    }, [
        hasSVGStringCache,
        vectorType,
        id,
        url,
        fill,
        fillGradient,
        stroke,
        strokeGradient,
        strokeWidth,
        strokeDasharray,
        strokeLinecap,
        width,
        height,
        naturalWidth,
        naturalHeight,
        effect, // 引用类型
        blur,
        strokeHidden,
        borderRadius, // 引用类型
    ]);

    const graphSVGImage = useMemo<SVGImage | null>(() => {
        if (!hasSVGStringCache || vectorType !== VectorType.Graph) return null;
        return createGraphSVGImage(svgStringCache, layer);
    }, [hasSVGStringCache, vectorType, colors, naturalWidth, naturalHeight]);

    const svgImage = normalSVGImage || graphSVGImage;
    const renderEllipse = vectorType === VectorType.Ellipse;
    const renderSVGCode = (vectorType === VectorType.Polygon || vectorType === VectorType.Rect) && isUsingEffect;

    useEffect(() => {
        if (vectorType === VectorType.Ellipse || vectorType === VectorType.Rect || hasSVGStringCache) {
            if (onRenderEnd) onRenderEnd();
        }
    }, [vectorType, onRenderEnd]);

    useEffect(() => {
        if (!url || hasSVGStringCache) {
            return;
        }
        fetchVectorText(url).then(() => {
            updateManual();
        });
    }, [url, hasSVGStringCache]);

    if (renderEllipse) {
        return (
            <div style={containerStyle}>
                <SVGRenderer
                    showDrawId={!asMenuIcon}
                    id={layer.id}
                    type={VectorType.Ellipse}
                    radius={borderRadius.leftBottom}
                    width={width}
                    height={height}
                    fill={fillGradient || fill}
                    stroke={strokeGradient || stroke}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray || ''}
                    strokeLinecap={strokeLinecap || ''}
                    blur={blur}
                    effect={effect}
                    strokeHidden={strokeHidden}
                />
            </div>
        );
    }

    if (!svgImage) return null;

    if (renderSVGCode) {
        return (
            <div style={containerStyle}>
                <div style={{ width: '100%', height: '100%' }} dangerouslySetInnerHTML={{ __html: svgImage.src }} />
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            {vectorType === VectorType.Graph || vectorType === VectorType.Polygon ? (
                // 使用父级缩放解决 Safari 展示异常问题
                <div
                    style={{
                        width: 0,
                        height: 0,
                        transform: `scale(${
                            originImgWidth / (svgImage.width || naturalWidth)
                        },${originImgHeight / (svgImage.height || naturalHeight)})`,
                    }}
                >
                    <img
                        {...(asMenuIcon ? {} : { 'data-draw-id': `svg-${layer.id}` })}
                        crossOrigin="anonymous"
                        width={svgImage.width || naturalWidth}
                        height={svgImage.height || naturalHeight}
                        style={{ display: 'block' }}
                        src={svgImage.src}
                    />
                </div>
            ) : (
                <img
                    {...(asMenuIcon ? {} : { 'data-draw-id': `svg-${layer.id}` })}
                    crossOrigin="anonymous"
                    width={svgImage.width || naturalWidth}
                    height={svgImage.height || naturalHeight}
                    style={{ display: 'block' }}
                    src={svgImage.src}
                />
            )}
        </div>
    );
};

export default ImageContent;
